import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { trackConversion } from "@/lib/analytics";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { UserCircle, Phone, Send, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useTranslatedContent } from "@/hooks/useTranslatedContent";
import { toast } from "sonner";

const WHATSAPP_LOGO_URL = "https://kxrduwrurqjvouiqobyc.supabase.co/storage/v1/object/public/photo-edm//WhatsApp.png";

const ContactPage = () => {
  const content = {
    title: "Contact",
    location: "Localisation",
    ourContacts: "Nos contacts",
    contactForm: "Nous contacter",
    yourName: "Votre nom",
    yourEmail: "Votre email",
    yourPhone: "Votre téléphone (optionnel)",
    subject: "Sujet",
    message: "Message",
    send: "Envoyer",
    sending: "Envoi en cours...",
    successMessage: "Votre message a été envoyé avec succès !",
    errorMessage: "Une erreur est survenue. Veuillez réessayer.",
    requiredFields: "Veuillez remplir tous les champs obligatoires."
  };

  const { translatedContent } = useTranslatedContent(content);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: admins } = useQuery({
    queryKey: ['contact-admins'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('display_on_contact', true);

      if (error) throw error;
      return data;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error(translatedContent.requiredFields);
      return;
    }

    setIsSubmitting(true);
    try {
      // Extract first and last name
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Create a case from the contact form
      const { error } = await supabase
        .from('cases')
        .insert({
          subject: formData.subject,
          description: formData.message,
          origin: 'contact_form',
          client_email: formData.email,
          client_name: formData.name,
          client_phone: formData.phone || null,
          status: 'new',
          priority: 'medium'
        });

      if (error) throw error;

      trackConversion("generate_lead", { method: "contact_form" });
      toast.success(translatedContent.successMessage);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(translatedContent.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <h1 className="text-3xl font-playfair text-villa-blue mb-8">{translatedContent.title}</h1>

        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <h2 className="text-2xl font-playfair text-villa-blue mb-4">{translatedContent.location}</h2>
            <div className="aspect-video rounded-lg overflow-hidden">
              <iframe
                title="Villa location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5783.5!2d4.130!3d43.543!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b6bb7d0c59c611%3A0x0!2sLe%20Boucanet%2C%20Le%20Grau-du-Roi!5e0!3m2!1sfr!2sfr!4v1711624153287!5m2!1sfr!2sfr"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="mt-4">
              <p className="text-lg">
                Villa Douce Vague<br />
                Quartier le Boucanet<br />
                30240 Le Grau-du-Roi<br />
                France
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-playfair text-villa-blue mb-4">{translatedContent.ourContacts}</h2>
            <div className="space-y-4">
              {admins?.map((admin) => (
                <Card key={admin.id}>
                  <CardContent className="flex items-center p-4">
                    <Avatar className="w-16 h-16">
                      <AvatarImage 
                        src={admin.profile_image ? 
                          supabase.storage
                            .from('profile_images')
                            .getPublicUrl(admin.profile_image)
                            .data.publicUrl 
                          : undefined
                        } 
                      />
                      <AvatarFallback>
                        <UserCircle className="w-8 h-8" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 ml-4">
                      <h3 className="font-medium">
                        {admin.first_name} {admin.last_name}
                      </h3>
                      {admin.phone && (
                        <div className="flex items-center text-sm text-gray-600 mt-1">
                          <Phone className="w-4 h-4 mr-1" />
                          <a href={`tel:${admin.phone}`} className="hover:underline">
                            {admin.phone}
                          </a>
                        </div>
                      )}
                    </div>
                    {admin.whatsapp_contact_url && (
                      <a
                        href={admin.whatsapp_contact_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-3 flex items-center justify-center"
                        title={`Contacter ${admin.first_name} ${admin.last_name} sur WhatsApp`}
                      >
                        <img
                          src={WHATSAPP_LOGO_URL}
                          alt="Logo WhatsApp"
                          className="w-10 h-10 object-contain"
                          style={{ borderRadius: "50%" }}
                        />
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="mt-16">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-playfair text-villa-blue">
                {translatedContent.contactForm}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">{translatedContent.yourName} *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Jean Dupont"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{translatedContent.yourEmail} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="jean.dupont@email.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="phone">{translatedContent.yourPhone}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">{translatedContent.subject} *</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Demande de renseignements"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{translatedContent.message} *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Votre message..."
                    rows={5}
                    required
                  />
                </div>

                <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {translatedContent.sending}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {translatedContent.send}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
